import { ArrowRight, CheckCircle2, Shield, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { DealExportIntelligence, ExportInsightItem } from "@/lib/deal-share-summary";
import {
  rewriteNarrativeConfidence,
  type ConfidenceEngineShape,
} from "@/lib/confidence";

interface InvestmentReadoutProps {
  intelligence: DealExportIntelligence;
  title?: string;
  compact?: boolean;
  /**
   * Canonical confidence engine. When provided, all narrative text rendered
   * by this component (bottom line, top risks, validated strengths,
   * recommended next step) is rewritten so any embedded confidence figures
   * match `confidence_engine.lane_breakdown` — the single source of truth.
   */
  engine?: ConfidenceEngineShape | null;
}

export function InvestmentReadout({
  intelligence,
  title = "Investment Readout",
  compact = false,
  engine = null,
}: InvestmentReadoutProps) {
  const rewrite = (text: string | undefined | null) =>
    engine ? rewriteNarrativeConfidence(text ?? "", engine) : (text ?? "");
  const rewriteItem = (item: ExportInsightItem): ExportInsightItem => ({
    ...item,
    title: rewrite(item.title),
    detail: item.detail ? rewrite(item.detail) : item.detail,
  });

  const rawReadout = intelligence.investment_readout;
  const readout = {
    ...rawReadout,
    bottom_line: rewrite(rawReadout.bottom_line),
    recommended_next_step: rewrite(rawReadout.recommended_next_step),
    top_risks: rawReadout.top_risks.map(rewriteItem),
    validated_strengths: rawReadout.validated_strengths.map(rewriteItem),
  };

  return (
    <Card className="rounded-lg border-border bg-card shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Investor-ready summary derived from validated document intelligence, trust signals, and evidence-backed interpretation.
            </p>
          </div>
          <Badge variant="outline" className="w-fit border-border bg-muted/50 text-foreground">
            <Shield className="mr-2 h-3.5 w-3.5 text-accent" />
            {readout.confidence_label} confidence · {readout.confidence_score}/100
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-border bg-muted/20 p-4">
          <p className="eyebrow text-muted-foreground">Bottom Line</p>
          <p className="mt-2 text-sm leading-6 text-foreground">{readout.bottom_line}</p>
        </div>

        <div className={`grid gap-4 ${compact ? "md:grid-cols-1" : "md:grid-cols-2"}`}>
          <section className="rounded-md border border-border bg-muted/20 p-4">
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-display text-sm font-semibold">Top 3 risks</h3>
            </div>
            <div className="space-y-3">
              {readout.top_risks.length > 0 ? readout.top_risks.map((item, index) => (
                <div key={`${item.title}-${index}`} className="rounded-md border border-border bg-card p-3">
                  <p className="text-sm text-foreground">{item.title}</p>
                  {(item.detail || item.source) && (
                    <p className="mt-1 text-xs text-muted-foreground">{[item.detail, item.source].filter(Boolean).join(" • ")}</p>
                  )}
                </div>
              )) : (
                <p className="text-xs text-muted-foreground">No material document risks surfaced yet.</p>
              )}
            </div>
          </section>

          <section className="rounded-md border border-border bg-muted/20 p-4">
            <div className="mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-display text-sm font-semibold">Top 2 validated strengths</h3>
            </div>
            <div className="space-y-3">
              {readout.validated_strengths.length > 0 ? readout.validated_strengths.map((item, index) => (
                <div key={`${item.title}-${index}`} className="rounded-md border border-border bg-card p-3">
                  <p className="text-sm text-foreground">{item.title}</p>
                  {(item.detail || item.source) && (
                    <p className="mt-1 text-xs text-muted-foreground">{[item.detail, item.source].filter(Boolean).join(" • ")}</p>
                  )}
                </div>
              )) : (
                <p className="text-xs text-muted-foreground">Validated strengths will appear once stronger corroborating evidence is present.</p>
              )}
            </div>
          </section>
        </div>

        <div className="rounded-md border border-border bg-muted/20 p-4">
          <div className="flex items-start gap-3">
            <ArrowRight className="mt-0.5 h-4 w-4 text-accent" />
            <div>
              <p className="eyebrow text-muted-foreground">Recommended Next Step</p>
              <p className="mt-2 text-sm text-foreground">{readout.recommended_next_step}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}